import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  MessageCircle,
  Bot,
  User,
  Phone,
  Globe,
  Clock,
  ChevronRight,
  ArrowLeft,
  Loader2,
  MessageSquare,
  TrendingUp,
  Users,
  RefreshCw,
  Wifi,
  UserCheck,
  PauseCircle,
  PlayCircle,
  Send,
  Search,
  X,
  BarChart2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Info,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

const satisfactionChartConfig = {
  satisfactionRate: { label: "Satisfação", color: "hsl(var(--chart-2))" },
} satisfies ChartConfig;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(ts: number | string | null | undefined) {
  if (!ts) return "—";
  return new Date(Number(ts)).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelative(ts: number | string | null | undefined) {
  if (!ts) return "—";
  const diff = Date.now() - Number(ts);
  if (diff < 60_000) return "agora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}min atrás`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h atrás`;
  return new Date(Number(ts)).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function isSessionActive(lastMessageAt: number | string | null | undefined) {
  if (!lastMessageAt) return false;
  return Date.now() - Number(lastMessageAt) < 10 * 60 * 1000;
}

function channelLabel(channel: string | null | undefined) {
  if (channel === "whatsapp") return { label: "WhatsApp", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
  if (channel === "instagram") return { label: "Instagram", color: "bg-pink-500/15 text-pink-400 border-pink-500/30" };
  if (channel === "web") return { label: "Chat Web", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" };
  return { label: "API", color: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" };
}

function getClientLabel(session: any) {
  if (session.clientName && session.clientPhone) return `${session.clientName} (${session.clientPhone})`;
  if (session.clientName) return session.clientName;
  if (session.clientPhone) return session.clientPhone;
  return session.clientIdentifier ?? "Cliente";
}

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

function priorityLabel(priority: string | null | undefined) {
  return ({ low: "Baixa", normal: "Normal", high: "Alta", urgent: "Urgente" } as Record<string, string>)[priority ?? "normal"] ?? "Normal";
}

function priorityClass(priority: string | null | undefined) {
  return ({
    low: "border-slate-500/30 bg-slate-500/10 text-slate-500",
    normal: "border-blue-500/30 bg-blue-500/10 text-blue-500",
    high: "border-amber-500/30 bg-amber-500/10 text-amber-600",
    urgent: "border-red-500/30 bg-red-500/10 text-red-600",
  } as Record<string, string>)[priority ?? "normal"] ?? "border-blue-500/30 bg-blue-500/10 text-blue-500";
}

function isSlaOverdue(session: any) {
  return !!session?.humanSlaDueAt && new Date(session.humanSlaDueAt).getTime() < Date.now();
}

function slaLabel(session: any) {
  if (!session?.humanPausedAt || !session?.humanSlaDueAt) return null;
  const diff = new Date(session.humanSlaDueAt).getTime() - Date.now();
  return diff <= 0 ? `SLA atrasado há ${Math.max(1, Math.floor(Math.abs(diff) / 60_000))}min` : `Responder em ${Math.max(1, Math.ceil(diff / 60_000))}min`;
}

function getMessageAuthor(message: any) {
  const type = message?.authorType;
  if (type === "customer" || ((type === "unknown" || !type) && message?.role === "user")) {
    return { label: "Cliente", kind: "customer" as const, outgoing: false };
  }
  if (type === "human") {
    const name = typeof message?.authorName === "string" && message.authorName.trim() ? ` · ${message.authorName.trim()}` : "";
    return { label: `Equipe${name}`, kind: "human" as const, outgoing: true };
  }
  if (type === "ai") {
    return { label: "IA · Assistente virtual", kind: "ai" as const, outgoing: true };
  }
  if (type === "system" || message?.role === "system") {
    return { label: "Sistema", kind: "system" as const, outgoing: false };
  }
  return { label: "Resposta registrada", kind: "unknown" as const, outgoing: true };
}

function getDeliveryLabel(message: any, channel: string | null | undefined, authorKind: ReturnType<typeof getMessageAuthor>["kind"]) {
  if (authorKind === "customer" || authorKind === "system") return null;
  if (authorKind === "unknown") {
    return { label: "Histórico sem rastreio de envio", className: "text-muted-foreground" };
  }
  const status = message?.deliveryStatus ?? "recorded";
  const channelName = channelLabel(message?.deliveryChannel ?? channel).label;
  if (status === "sent") {
    if (message?.deliveryReceiptStatus === "read") {
      return { label: `Lida no ${channelName} (receipt do provedor)`, className: "text-emerald-600 dark:text-emerald-400" };
    }
    if (message?.deliveryReceiptStatus === "delivered") {
      return { label: `Entregue no ${channelName} (receipt do provedor)`, className: "text-emerald-600 dark:text-emerald-400" };
    }
    return { label: `Enviada via ${channelName}`, className: "text-emerald-600 dark:text-emerald-400" };
  }
  if (status === "failed") {
    return { label: "Falhou ao enviar no canal", className: "text-red-600 dark:text-red-400" };
  }
  return { label: "Apenas registrada no sistema", className: "text-amber-700 dark:text-amber-400" };
}

function OmnichannelMessageBubble({ message, channel, compact = false }: { message: any; channel?: string | null; compact?: boolean }) {
  const author = getMessageAuthor(message);
  const delivery = getDeliveryLabel(message, channel, author.kind);
  const isHuman = author.kind === "human";
  const isAi = author.kind === "ai";
  const isCustomer = author.kind === "customer";
  const avatarSizeClass = compact ? "w-6 h-6" : "w-7 h-7";
  const iconSizeClass = compact ? "w-3 h-3" : "w-3.5 h-3.5";
  const avatarClass = isAi ? "bg-violet-500/15" : isHuman ? "bg-amber-500/15" : isCustomer ? "bg-primary/15" : "bg-muted";
  const bubbleClass = isAi
    ? "bg-violet-500/10 border border-violet-500/15 rounded-tl-sm"
    : isHuman
      ? "bg-amber-500/10 border border-amber-500/20 rounded-tl-sm"
      : isCustomer
        ? "bg-primary/15 rounded-tr-sm"
        : "bg-muted rounded-tl-sm";

  return (
    <div className={`flex gap-2.5 ${isCustomer ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`${avatarSizeClass} rounded-full flex items-center justify-center shrink-0 mt-0.5 ${avatarClass}`}>
        {isAi ? <Bot className={`${iconSizeClass} text-violet-500`} /> : isHuman ? <UserCheck className={`${iconSizeClass} text-amber-600 dark:text-amber-400`} /> : isCustomer ? <User className={`${iconSizeClass} text-primary`} /> : <MessageSquare className={`${iconSizeClass} text-muted-foreground`} />}
      </div>
      <div className={`${compact ? "max-w-[80%] px-3 py-2" : "max-w-[75%] px-3.5 py-2.5"} rounded-2xl ${bubbleClass}`}>
        <p className="text-[11px] font-semibold text-foreground/80">{author.label}</p>
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{message?.content}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
          <span>{formatDate(message?.createdAt)}</span>
          {isAi && message?.processingMs ? <span className="opacity-60">· {message.processingMs}ms</span> : null}
          {delivery ? <span className={`font-medium ${delivery.className}`} aria-label={`Status de envio: ${delivery.label}`}>· {delivery.label}</span> : null}
        </div>
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminAiConversations() {
  const { user } = useAuth();
  const isReceptionist = (user as any)?.teamRole === "receptionist" || (user as any)?.teamRole === "recepcionista";
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [lastSeenCounts, setLastSeenCounts] = useState<Record<number, number>>({});
  const [manualMessage, setManualMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [channelFilter, setChannelFilter] = useState<"all" | "whatsapp" | "instagram" | "web" | "api">("all");
  const [humanOnly, setHumanOnly] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<"all" | "low" | "normal" | "high" | "urgent">("all");
  const [statsPeriod, setStatsPeriod] = useState<"today" | "week" | "month" | "all">("all");
  const [statsOpen, setStatsOpen] = useState(false);
  const [learningNote, setLearningNote] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const initialLoadDoneRef = useRef(false);
  const utils = trpc.useUtils();

  const { data: sessions, isLoading: loadingSessions, refetch: refetchSessions } = trpc.salon.getAiSessions.useQuery(
    { limit: 100 },
    { refetchInterval: 5_000 }
  );
  const { data: staffMembers } = trpc.staff.list.useQuery(undefined, { enabled: !isReceptionist });

  const { data: stats, isLoading: loadingStats, refetch: refetchStats } = trpc.salon.getAiAgentStats.useQuery(
    { period: statsPeriod },
    { refetchInterval: 15_000 }
  );
  const { data: satisfactionTimeline, refetch: refetchSatisfactionTimeline } = trpc.salon.getAiAgentSatisfactionTimeline.useQuery(
    { period: statsPeriod },
    { refetchInterval: 15_000 }
  );

  const { data: messages, isLoading: loadingMessages } = trpc.salon.getAiSessionMessages.useQuery(
    { sessionId: selectedSessionId! },
    { enabled: selectedSessionId !== null, refetchInterval: 3_000 }
  );

  const pauseMutation = trpc.salon.pauseAiSession.useMutation({
    onSuccess: () => {
      utils.salon.getAiSessions.invalidate();
      toast.success("Conversa assumida", { description: "Agente pausado. Você está no controle." });
    },
    onError: (e) => toast.error("Erro", { description: e.message }),
  });

  const resumeMutation = trpc.salon.resumeAiSession.useMutation({
    onSuccess: () => {
      utils.salon.getAiSessions.invalidate();
      toast.success("Agente retomado", { description: "O agente voltou a responder automaticamente." });
    },
    onError: (e) => toast.error("Erro", { description: e.message }),
  });

  const sendManualMutation = trpc.salon.sendManualMessage.useMutation({
    onSuccess: (result) => {
      setManualMessage("");
      utils.salon.getAiSessionMessages.invalidate({ sessionId: selectedSessionId! });
      utils.salon.getAiSessions.invalidate();
      const channel = channelLabel(result.channel).label;
      if (result.deliveryStatus === "sent") {
        toast.success(`Mensagem enviada via ${channel}`);
      } else if (result.deliveryStatus === "failed") {
        toast.error("Mensagem registrada, mas não foi enviada no canal");
      } else {
        toast.message("Mensagem registrada no sistema", { description: "Não houve envio externo para esta conversa." });
      }
    },
    onError: (e) => toast.error("Erro ao enviar", { description: e.message }),
  });
  const updateQueueMutation = trpc.salon.updateAiSessionQueue.useMutation({
    onSuccess: () => {
      utils.salon.getAiSessions.invalidate();
      toast.success("Fila atualizada");
    },
    onError: (e) => toast.error("Não foi possível atualizar a fila", { description: e.message }),
  });
  const updateLearningMutation = trpc.salon.updateAiSessionLearning.useMutation({
    onSuccess: () => {
      utils.salon.getAiSessions.invalidate();
      toast.success("Aprendizado salvo", { description: "A regra aprovada será considerada pelo agente em conversas futuras." });
    },
    onError: (e) => toast.error("Não foi possível salvar", { description: e.message }),
  });
  const updateRecoveryConsentMutation = trpc.salon.updateAiSessionRecoveryConsent.useMutation({
    onSuccess: () => {
      utils.salon.getAiSessions.invalidate();
      toast.success("Preferência de retomada atualizada");
    },
    onError: (e) => toast.error("Não foi possível atualizar", { description: e.message }),
  });

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const threshold = 80;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    isAtBottomRef.current = distanceFromBottom <= threshold;
  };

  useEffect(() => {
    if (!messages || !messagesEndRef.current) return;
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;
    const isNewMessage = currentCount > prevCount;
    prevMessageCountRef.current = currentCount;

    // Primeiro carregamento: vai para o final sem animação
    if (!initialLoadDoneRef.current && currentCount > 0) {
      initialLoadDoneRef.current = true;
      messagesEndRef.current.scrollIntoView({ behavior: "instant" });
      return;
    }

    // Nova mensagem chegou: só rola se o usuário já estava no final
    if (isNewMessage && isAtBottomRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    isAtBottomRef.current = true;
    prevMessageCountRef.current = 0;
    initialLoadDoneRef.current = false;
    setManualMessage("");
    setLearningNote("");
  }, [selectedSessionId]);

  useEffect(() => {
    if (selectedSessionId && sessions) {
      const session = sessions.find((s) => s.id === selectedSessionId);
      if (session) {
        setLastSeenCounts((prev) => ({
          ...prev,
          [selectedSessionId]: session.messageCount ?? 0,
        }));
      }
    }
  }, [selectedSessionId, sessions]);

  const selectedSession = sessions?.find((s) => s.id === selectedSessionId);
  const isHumanMode = !!(selectedSession as any)?.humanPausedAt;

  const filteredSessions = sessions?.filter((s) => {
    if (channelFilter !== "all" && (s as any).channel !== channelFilter) return false;
    if (humanOnly && !(s as any).humanPausedAt) return false;
    if (priorityFilter !== "all" && ((s as any).priority ?? "normal") !== priorityFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const label = getClientLabel(s as any).toLowerCase();
    const name = ((s as any).clientName ?? "").toLowerCase();
    const phone = ((s as any).clientPhone ?? "").toLowerCase();
    const identifier = ((s as any).clientIdentifier ?? "").toLowerCase();
    return label.includes(q) || name.includes(q) || phone.includes(q) || identifier.includes(q);
  }).sort((a, b) => {
    const first = a as any;
    const second = b as any;
    if (isSlaOverdue(first) !== isSlaOverdue(second)) return isSlaOverdue(first) ? -1 : 1;
    if (!!first.humanPausedAt !== !!second.humanPausedAt) return first.humanPausedAt ? -1 : 1;
    return (PRIORITY_ORDER[first.priority ?? "normal"] ?? 2) - (PRIORITY_ORDER[second.priority ?? "normal"] ?? 2);
  }) ?? [];

  const channelCounts = {
    all: sessions?.length ?? 0,
    whatsapp: sessions?.filter((session) => (session as any).channel === "whatsapp").length ?? 0,
    instagram: sessions?.filter((session) => (session as any).channel === "instagram").length ?? 0,
    web: sessions?.filter((session) => (session as any).channel === "web").length ?? 0,
    api: sessions?.filter((session) => (session as any).channel === "api").length ?? 0,
  };

  const unreadSessions = sessions?.filter((s) => {
    const lastSeen = lastSeenCounts[s.id] ?? 0;
    return (s.messageCount ?? 0) > lastSeen;
  }) ?? [];

  const activeSessions = sessions?.filter((s) => isSessionActive((s as any).lastMessageAt)) ?? [];
  const humanModeSessions = sessions?.filter((s) => !!(s as any).humanPausedAt) ?? [];
  const overdueSessions = humanModeSessions.filter((session) => isSlaOverdue(session as any));
  const staffById = new Map((staffMembers ?? []).map((staff: any) => [staff.id, staff]));
  const feedbackCount = (stats as any)?.feedbackCount ?? 0;
  const satisfactionRate = (stats as any)?.satisfactionRate ?? 0;
  const satisfactionGuidance = feedbackCount === 0
    ? "Ainda não há avaliações. Ative e acompanhe o feedback rápido para medir a experiência."
    : satisfactionRate < 70
      ? "Atenção: reveja conversas recentes e crie regras aprovadas para as dúvidas mais frequentes."
      : satisfactionRate < 90
        ? "Em acompanhamento: identifique objeções recorrentes para elevar a satisfação."
        : "Bom resultado: mantenha as regras aprovadas e acompanhe a tendência no gráfico.";
  const handoffRate = Number((stats as any)?.handoffRate ?? 0);
  const fallbackRate = Number((stats as any)?.fallbackRate ?? 0);
  const fallbackCount = Number((stats as any)?.fallbackCount ?? 0);
  const aiResponseCount = Number((stats as any)?.aiResponseCount ?? 0);
  const averageProcessingMs = Number((stats as any)?.averageProcessingMs ?? 0);
  const slowResponseCount = Number((stats as any)?.slowResponseCount ?? 0);
  const operationalAlert = (stats as any)?.operationalAlert ?? "none";
  const handoffGuidance = handoffRate >= 35
    ? "Atenção: muitas conversas estão sendo transferidas. Revise as dúvidas recorrentes e aprove novas regras para a IA."
    : handoffRate >= 15
      ? "Em acompanhamento: observe os motivos de transferência e mantenha a equipe disponível nos horários de maior demanda."
      : "Fluxo saudável: a maior parte das conversas permanece com o agente sem perder o acompanhamento humano.";
  const handleSendManual = () => {
    if (!manualMessage.trim() || !selectedSessionId) return;
    sendManualMutation.mutate({ sessionId: selectedSessionId, message: manualMessage.trim() });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendManual();
    }
  };

  // ─── Mobile: tela de chat aberta ─────────────────────────────────────────────
  const MobileChatView = () => (
    <div className="fixed inset-0 z-50 bg-background flex flex-col md:hidden">
      {/* Header do chat */}
      <div className="flex items-center gap-3 px-3 py-2.5 border-b border-border/50 bg-card flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => setSelectedSessionId(null)}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="relative shrink-0">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            {(selectedSession as any)?.channel === "whatsapp" ? (
              <Phone className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Globe className="w-3.5 h-3.5 text-blue-400" />
            )}
          </div>
          {isSessionActive((selectedSession as any)?.lastMessageAt) && (
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-background" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {getClientLabel(selectedSession as any)}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {channelLabel((selectedSession as any)?.channel).label}
            {" · "}{(selectedSession as any)?.messageCount ?? 0} msgs
            {isSessionActive((selectedSession as any)?.lastMessageAt) && (
              <span className="ml-1 text-emerald-400">● ativa</span>
            )}
          </p>
        </div>
        {/* Botão Assumir / Devolver */}
        {!isHumanMode ? (
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-xs text-amber-400 border-amber-500/40 hover:bg-amber-500/10 h-8 px-2.5 shrink-0"
            onClick={() => pauseMutation.mutate({ sessionId: selectedSessionId! })}
            disabled={pauseMutation.isPending}
          >
            {pauseMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <PauseCircle className="w-3 h-3" />}
            Assumir
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-xs text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10 h-8 px-2.5 shrink-0"
            onClick={() => resumeMutation.mutate({ sessionId: selectedSessionId! })}
            disabled={resumeMutation.isPending}
          >
            {resumeMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
            Devolver IA
          </Button>
        )}
      </div>

      {/* Mensagens */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        {loadingMessages ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-muted-foreground">Nenhuma mensagem nesta sessão</p>
          </div>
        ) : (
          <div className="p-3 space-y-2.5 pb-4">
            {messages
              .filter((msg) => (msg as any).role !== "system")
              .map((msg) => <OmnichannelMessageBubble key={msg.id} message={msg} channel={(selectedSession as any)?.channel} compact />)}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input modo humano */}
      {isHumanMode && (
        <div className="shrink-0 border-t border-border/50 p-3 bg-amber-500/5 pb-safe">
          <div className="flex items-center gap-1 mb-1.5">
            <UserCheck className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-amber-400 font-medium">Modo Humano — você está respondendo</span>
          </div>
          <div className="flex gap-2">
            <Textarea
              value={manualMessage}
              onChange={(e) => setManualMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua mensagem..."
              className="resize-none text-sm min-h-[52px] max-h-[100px] bg-background/60"
              disabled={sendManualMutation.isPending}
            />
            <Button
              size="icon"
              className="h-auto w-10 self-end bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleSendManual}
              disabled={!manualMessage.trim() || sendManualMutation.isPending}
            >
              {sendManualMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* ── Mobile: chat aberto em tela cheia ── */}
      {selectedSessionId !== null && <MobileChatView />}

      <div className="flex flex-col h-full">
        {/* ── Header mobile compacto ── */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 md:px-6 md:pt-5 md:pb-4 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-violet-400" />
            </div>
            <div>
              <h1 className="text-base md:text-xl font-semibold text-foreground flex items-center gap-2">
                Central Omnichannel
                {activeSessions.length > 0 && (
                  <span className="flex items-center gap-1 text-[10px] md:text-xs font-normal text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">
                    <Wifi className="w-2.5 h-2.5" />
                    {activeSessions.length} ativa{activeSessions.length > 1 ? "s" : ""}
                  </span>
                )}
                {humanModeSessions.length > 0 && (
                  <span className="flex items-center gap-1 text-[10px] md:text-xs font-normal text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded-full">
                    <UserCheck className="w-2.5 h-2.5" />
                    {humanModeSessions.length} humano
                  </span>
                )}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Botão estatísticas — mobile */}
            <button
              onClick={() => setStatsOpen((v) => !v)}
              className="md:hidden flex items-center gap-1 text-xs text-muted-foreground border border-border/50 rounded-lg px-2.5 py-1.5 hover:bg-muted/40 transition-colors"
            >
              <BarChart2 className="w-3.5 h-3.5" />
              Stats
              {statsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { refetchSessions(); refetchStats(); refetchSatisfactionTimeline(); }}
              className="gap-1.5 h-8 text-xs hidden md:flex"
            >
              <RefreshCw className="w-3 h-3" />
              Atualizar
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { refetchSessions(); }}
              className="md:hidden h-8 w-8"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* ── Stats colapsáveis (mobile) / sempre visíveis (desktop) ── */}
        <div className={`px-4 md:px-6 flex-shrink-0 overflow-hidden transition-all duration-300 ${statsOpen ? "max-h-96 pb-3" : "max-h-0 md:max-h-96 md:pb-4"}`}>
          {/* Filtro de período */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            <span className="text-xs text-muted-foreground font-medium">Período:</span>
            {(["today", "week", "month", "all"] as const).map((p) => (
              <button
                key={p}
                onClick={() => setStatsPeriod(p)}
                className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                  statsPeriod === p
                    ? "bg-violet-500 text-white border-violet-500"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                }`}
              >
                {p === "today" ? "Hoje" : p === "week" ? "Semana" : p === "month" ? "Mês" : "Total"}
              </button>
            ))}
          </div>

          {/* Cards de métricas */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <Card className="border-border/50" title="Quantidade de conversas iniciadas no período selecionado.">
              <CardContent className="p-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
                  <Users className="w-3.5 h-3.5 text-violet-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground leading-tight">Sessões iniciadas</p>
                  <p className="text-lg font-bold text-foreground">{loadingStats ? "—" : (stats?.totalSessions ?? 0)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50" title="Total de mensagens trocadas entre clientes, agente e equipe no período.">
              <CardContent className="p-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <MessageSquare className="w-3.5 h-3.5 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground leading-tight">Mensagens</p>
                  <p className="text-lg font-bold text-foreground">{loadingStats ? "—" : (stats?.totalMessages ?? 0)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50" title="Conversas que continuam abertas no sistema. Não representa pessoas conversando ao vivo neste instante.">
              <CardContent className="p-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground leading-tight">Sessões abertas</p>
                  <p className="text-lg font-bold text-foreground">{loadingStats ? "—" : (stats?.activeSessions ?? 0)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border-border/50 ${(stats as any)?.humanModeSessions > 0 ? "border-amber-500/40 bg-amber-500/5" : ""}`} title="Conversas assumidas pela equipe, com o agente pausado.">
              <CardContent className="p-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                  <UserCheck className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground leading-tight">Humano</p>
                  <p className={`text-lg font-bold ${(stats as any)?.humanModeSessions > 0 ? "text-amber-400" : "text-foreground"}`}>
                    {(stats as any)?.humanModeSessions ?? humanModeSessions.length}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50" title="Mostra transferências ao humano, regras aprovadas e satisfação das clientes.">
              <CardContent className="p-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground leading-tight flex items-center gap-1">Qualidade e transbordo <Info className="w-2.5 h-2.5" /></p>
                  <p className="text-sm font-bold text-foreground">{handoffRate}% para humano</p>
                  <p className="text-[10px] text-muted-foreground">{(stats as any)?.approvedLearnings ?? 0} regra(s) aprovada(s)</p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400">{satisfactionRate}% satisfação · {feedbackCount} resposta(s)</p>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className={`mt-2 flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] ${feedbackCount === 0 ? "border-blue-500/20 bg-blue-500/5 text-blue-700 dark:text-blue-300" : satisfactionRate < 70 ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300" : "border-emerald-500/20 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"}`}>
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>{satisfactionGuidance}</p>
          </div>
          <div className={`mt-2 flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] ${handoffRate >= 35 ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300" : "border-blue-500/20 bg-blue-500/5 text-blue-700 dark:text-blue-300"}`}>
            <UserCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p><strong>Transferências para humano:</strong> {handoffGuidance}</p>
          </div>
          <div className={`mt-2 flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] ${operationalAlert === "critical" ? "border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-300" : operationalAlert === "attention" ? "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300" : "border-emerald-500/20 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"}`}>
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
              <strong>Operação da IA:</strong>{" "}
              {aiResponseCount === 0
                ? "ainda não há respostas no período selecionado."
                : operationalAlert === "critical"
                  ? `${fallbackRate}% das respostas precisaram de recuperação ou fallback. Assuma as conversas recentes e revise as integrações.`
                  : operationalAlert === "attention"
                    ? `${fallbackRate}% de fallback, média de ${(averageProcessingMs / 1000).toFixed(1).replace(".", ",")}s e ${slowResponseCount} resposta(s) lenta(s). Acompanhe as conversas recentes.`
                    : `operação estável: ${fallbackCount} fallback(s) em ${aiResponseCount} resposta(s), média de ${(averageProcessingMs / 1000).toFixed(1).replace(".", ",")}s.`}
            </p>
          </div>
          <Card className="mt-3 border-border/50">
            <CardHeader className="py-3 px-3.5">
              <CardTitle className="text-xs font-semibold flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> Evolução da satisfação
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-3">
              {satisfactionTimeline && satisfactionTimeline.length > 0 ? (
                <ChartContainer config={satisfactionChartConfig} className="h-36 w-full">
                  <LineChart accessibilityLayer data={satisfactionTimeline} margin={{ left: -18, right: 8, top: 5, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} fontSize={10} />
                    <YAxis domain={[0, 100]} tickLine={false} axisLine={false} tickFormatter={(value) => `${value}%`} fontSize={10} />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent labelFormatter={(label) => `Dia ${label}`} formatter={(value) => [`${value}%`, "Satisfação"]} />} />
                    <Line type="monotone" dataKey="satisfactionRate" stroke="var(--color-satisfactionRate)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 4 }} />
                  </LineChart>
                </ChartContainer>
              ) : (
                <div className="h-24 flex flex-col items-center justify-center text-center px-4">
                  <p className="text-xs font-medium text-foreground">Ainda não há avaliações neste período</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Quando as clientes responderem à pergunta rápida, a evolução aparecerá aqui.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {selectedSession && !isReceptionist && (
          <div className="px-4 md:px-6 pb-3 flex-shrink-0">
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2 min-w-0 sm:w-56">
                  <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold">Aprendizado supervisionado</p>
                    <p className="text-[10px] text-muted-foreground">Transforme esta conversa em uma regra aprovada.</p>
                  </div>
                </div>
                <Input
                  value={learningNote}
                  onChange={(event) => setLearningNote(event.target.value)}
                  placeholder="Ex.: Para X, informe Y e ofereça Z."
                  className="h-8 text-xs flex-1 bg-background"
                  maxLength={1200}
                />
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="h-8 text-xs" disabled={!learningNote.trim() || updateLearningMutation.isPending} onClick={() => updateLearningMutation.mutate({ sessionId: selectedSessionId!, status: "approved", note: learningNote })}>
                    Aprovar regra
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={updateLearningMutation.isPending} onClick={() => updateLearningMutation.mutate({ sessionId: selectedSessionId!, status: "dismissed" })}>
                    Dispensar
                  </Button>
                </div>
              </CardContent>
            </Card>
            <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-background/50 px-3 py-2">
              <p className="text-[11px] text-muted-foreground">Retomar esta conversa apenas se a cliente autorizou receber lembretes.</p>
              <Button
                size="sm"
                variant={(selectedSession as any)?.recoveryConsent ? "secondary" : "outline"}
                className="h-7 text-[11px] shrink-0"
                disabled={updateRecoveryConsentMutation.isPending}
                onClick={() => updateRecoveryConsentMutation.mutate({ sessionId: selectedSessionId!, consent: !(selectedSession as any)?.recoveryConsent })}
              >
                {(selectedSession as any)?.recoveryConsent ? "Retomada autorizada" : "Autorizar retomada"}
              </Button>
            </div>
          </div>
        )}

        {/* ── Conteúdo principal ── */}
        <div className="flex-1 min-h-0 px-4 md:px-6 pb-4 md:pb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">

            {/* ── Lista de sessões ── */}
            <Card className="border-border/50 flex flex-col overflow-hidden md:h-full h-[calc(100vh-180px)]">
              <CardHeader className="pb-2 pt-3 px-3 flex-shrink-0 space-y-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" />
                    Sessões ({filteredSessions.length}{searchQuery ? ` de ${sessions?.length ?? 0}` : ""})
                  </CardTitle>
                  {unreadSessions.length > 0 && (
                    <Badge className="h-5 px-1.5 bg-violet-500 text-white text-[10px]">
                      {unreadSessions.length} nova{unreadSessions.length > 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por nome, telefone..."
                    className="h-8 pl-8 pr-7 text-xs"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                  {([
                    { value: "all", label: "Todos" },
                    { value: "whatsapp", label: "WhatsApp" },
                    { value: "instagram", label: "Instagram" },
                    { value: "web", label: "Web" },
                  ] as const).map((channel) => (
                    <button key={channel.value} type="button" onClick={() => setChannelFilter(channel.value)} className={`shrink-0 rounded-full border px-2 py-1 text-[10px] transition-colors ${channelFilter === channel.value ? "border-violet-500 bg-violet-500 text-white" : "border-border text-muted-foreground hover:bg-muted"}`}>
                      {channel.label} {channelCounts[channel.value]}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setHumanOnly((value) => !value)}
                    className={`shrink-0 rounded-full border px-2 py-1 text-[10px] transition-colors ${humanOnly ? "border-amber-500 bg-amber-500 text-white" : "border-amber-500/40 text-amber-500 hover:bg-amber-500/10"}`}
                  >
                    Humano {humanModeSessions.length}
                  </button>
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                  {(["all", "urgent", "high", "normal", "low"] as const).map((priority) => (
                    <button key={priority} type="button" onClick={() => setPriorityFilter(priority)} className={`shrink-0 rounded-full border px-2 py-1 text-[10px] transition-colors ${priorityFilter === priority ? "border-violet-500 bg-violet-500 text-white" : "border-border text-muted-foreground hover:bg-muted"}`}>
                      {priority === "all" ? "Prioridade" : priorityLabel(priority)}
                    </button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-hidden">
                {loadingSessions ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : !sessions || sessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 gap-2 text-center px-4">
                    <Bot className="w-8 h-8 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">Nenhuma conversa ainda</p>
                  </div>
                ) : filteredSessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 gap-2 text-center px-4">
                    <Search className="w-8 h-8 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">Nenhum resultado</p>
                  </div>
                ) : (
                  <ScrollArea className="h-full">
                    <div className="divide-y divide-border/30">
                      {filteredSessions.map((session) => {
                        const ch = channelLabel((session as any).channel);
                        const isSelected = session.id === selectedSessionId;
                        const active = isSessionActive((session as any).lastMessageAt);
                        const lastSeen = lastSeenCounts[session.id] ?? 0;
                        const hasUnread = (session.messageCount ?? 0) > lastSeen && !isSelected;
                        const sessionAny = session as any;
                        const assignedStaff = sessionAny.assignedStaffId ? staffById.get(sessionAny.assignedStaffId) : null;
                        const overdue = isSlaOverdue(sessionAny);
                        return (
                          <button
                            key={session.id}
                            onClick={() => setSelectedSessionId(session.id)}
                            className={`w-full text-left px-3 py-2.5 hover:bg-muted/40 transition-colors flex items-start gap-2.5 ${isSelected ? "bg-muted/60" : ""} ${hasUnread ? "bg-violet-500/5" : ""}`}
                          >
                            <div className="relative shrink-0 mt-0.5">
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                {(session as any).channel === "whatsapp" ? (
                                  <Phone className="w-3.5 h-3.5 text-emerald-400" />
                                ) : (
                                  <Globe className="w-3.5 h-3.5 text-blue-400" />
                                )}
                              </div>
                              {active && (
                                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-background" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-1">
                                <p className={`text-sm truncate ${hasUnread ? "font-semibold text-foreground" : "text-foreground"}`}>
                                  {getClientLabel(session as any)}
                                </p>
                                <div className="flex items-center gap-1 shrink-0">
                                  {hasUnread && <span className="w-2 h-2 rounded-full bg-violet-500" />}
                                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                {(session as any).humanPausedAt && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border bg-amber-500/20 text-amber-300 border-amber-500/40 flex items-center gap-0.5">
                                    <PauseCircle className="w-2.5 h-2.5" />
                                    Humano
                                  </Badge>
                                )}
                                {(session as any).autoClosedAt && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border bg-slate-500/10 text-slate-600 border-slate-500/30 flex items-center gap-0.5">
                                    <Clock className="w-2.5 h-2.5" />
                                    Encerrada automaticamente
                                  </Badge>
                                )}
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 border ${priorityClass(sessionAny.priority)}`}>{priorityLabel(sessionAny.priority)}</Badge>
                                {assignedStaff && <span className="max-w-24 truncate text-[10px] text-muted-foreground">{(assignedStaff as any).name}</span>}
                                {overdue && <span className="text-[10px] font-medium text-red-600">SLA atrasado</span>}
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 border ${ch.color}`}>
                                  {ch.label}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground">
                                  {(session as any).messageCount ?? 0} msgs
                                </span>
                                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                  <Clock className="w-2.5 h-2.5" />
                                  {formatRelative((session as any).lastMessageAt)}
                                </span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* ── Thread de mensagens (desktop only) ── */}
            <Card className="md:col-span-2 border-border/50 flex-col overflow-hidden hidden md:flex">
              {!selectedSessionId ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
                  <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                    <MessageCircle className="w-7 h-7 text-muted-foreground/40" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">Selecione uma conversa</p>
                  <p className="text-xs text-muted-foreground/60">Clique em uma sessão à esquerda para ver o histórico</p>
                </div>
              ) : (
                <>
                  <CardHeader className="pb-3 flex-shrink-0 border-b border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          {(selectedSession as any)?.channel === "whatsapp" ? (
                            <Phone className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Globe className="w-3.5 h-3.5 text-blue-400" />
                          )}
                        </div>
                        {isSessionActive((selectedSession as any)?.lastMessageAt) && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-background" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">
                            {getClientLabel(selectedSession as any)}
                          </p>
                          {isHumanMode && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border bg-amber-500/20 text-amber-300 border-amber-500/40 flex items-center gap-0.5">
                              <PauseCircle className="w-2.5 h-2.5" />
                              Atend. Humano
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {channelLabel((selectedSession as any)?.channel).label}
                          {" · "}{(selectedSession as any)?.messageCount ?? 0} mensagens
                          {isSessionActive((selectedSession as any)?.lastMessageAt) && (
                            <span className="ml-2 text-emerald-400">● Em conversa</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isHumanMode ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 text-amber-400 border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-300"
                            onClick={() => pauseMutation.mutate({ sessionId: selectedSessionId! })}
                            disabled={pauseMutation.isPending}
                          >
                            {pauseMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PauseCircle className="w-3.5 h-3.5" />}
                            Assumir
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10 hover:text-emerald-300"
                            onClick={() => resumeMutation.mutate({ sessionId: selectedSessionId! })}
                            disabled={resumeMutation.isPending}
                          >
                            {resumeMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                            Devolver à IA
                          </Button>
                        )}
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <RefreshCw className="w-3 h-3 animate-spin opacity-50" />
                          <span>ao vivo</span>
                        </div>
                      </div>
                    </div>
                    {!isReceptionist && <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/50 pt-3">
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Fila</span>
                      <Select value={(selectedSession as any)?.priority ?? "normal"} onValueChange={(priority) => updateQueueMutation.mutate({ sessionId: selectedSessionId!, priority: priority as "low" | "normal" | "high" | "urgent" })} disabled={updateQueueMutation.isPending}>
                        <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgente</SelectItem>
                          <SelectItem value="high">Alta</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="low">Baixa</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={(selectedSession as any)?.assignedStaffId ? String((selectedSession as any).assignedStaffId) : "unassigned"} onValueChange={(value) => updateQueueMutation.mutate({ sessionId: selectedSessionId!, assignedStaffId: value === "unassigned" ? null : Number(value) })} disabled={updateQueueMutation.isPending}>
                        <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder="Responsável" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Sem responsável</SelectItem>
                          {(staffMembers ?? []).map((staff: any) => <SelectItem key={staff.id} value={String(staff.id)}>{staff.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {slaLabel(selectedSession as any) && <Badge variant="outline" className={`text-[10px] ${isSlaOverdue(selectedSession as any) ? "border-red-500/30 bg-red-500/10 text-red-600" : "border-amber-500/30 bg-amber-500/10 text-amber-600"}`}><Clock className="mr-1 h-3 w-3" />{slaLabel(selectedSession as any)}</Badge>}
                    </div>}
                  </CardHeader>

                  <CardContent className="p-0 flex-1 overflow-hidden flex flex-col">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-32">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : !messages || messages.length === 0 ? (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-sm text-muted-foreground">Nenhuma mensagem nesta sessão</p>
                      </div>
                    ) : (
                      <div
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        className="flex-1 overflow-y-auto"
                      >
                        <div className="p-4 space-y-3">
                          {messages
                            .filter((msg) => (msg as any).role !== "system")
                            .map((msg) => <OmnichannelMessageBubble key={msg.id} message={msg} channel={(selectedSession as any)?.channel} />)}
                          <div ref={messagesEndRef} />
                        </div>
                      </div>
                    )}

                    {isHumanMode && (
                      <div className="flex-shrink-0 border-t border-border/50 p-3 bg-amber-500/5">
                        <div className="flex items-center gap-1 mb-2">
                          <UserCheck className="w-3.5 h-3.5 text-amber-400" />
                          <span className="text-[11px] text-amber-400 font-medium">Modo Atendimento Humano — você está respondendo</span>
                        </div>
                        <div className="flex gap-2">
                          <Textarea
                            value={manualMessage}
                            onChange={(e) => setManualMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Digite sua mensagem... (Enter para enviar)"
                            className="resize-none text-sm min-h-[60px] max-h-[120px] bg-background/60"
                            disabled={sendManualMutation.isPending}
                          />
                          <Button
                            size="icon"
                            className="h-auto w-10 self-end bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={handleSendManual}
                            disabled={!manualMessage.trim() || sendManualMutation.isPending}
                          >
                            {sendManualMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
